import { useCallback, useRef } from 'react';

type FocusableEl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function cellKey(row: number, col: number): string {
  return `${row}-${col}`;
}

// selectionStart/selectionEnd throw (or return null) on non-text <input>
// types (number, email, date...) — treat those, and non-<input> elements
// like <select>, as always "at the boundary" so Left/Right moves cells
// immediately instead of crashing or getting stuck.
function caretAtBoundary(el: FocusableEl): { atStart: boolean; atEnd: boolean } {
  if (el.tagName !== 'INPUT') return { atStart: true, atEnd: true };
  try {
    const input = el as HTMLInputElement;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (start == null || end == null) return { atStart: true, atEnd: true };
    const len = input.value.length;
    return { atStart: start === 0 && end === 0, atEnd: start === len && end === len };
  } catch {
    return { atStart: true, atEnd: true };
  }
}

/**
 * Excel-style arrow-key navigation across a grid of editable cells (an
 * <input>/EntityCombobox per [row, col]). Nothing like this existed
 * anywhere in the app before — see Purchase.tsx's Loose Polished Line
 * Items grid for the first consumer.
 *
 * ArrowUp/Down always move focus to the same column in the prev/next row
 * and preventDefault() — required even for a type="number" input, which
 * otherwise intercepts Up/Down to change its own value instead of moving
 * focus.
 *
 * ArrowLeft/Right move to the prev/next cell in the same row once the
 * caret is at the text boundary (start for Left, end for Right) on a text
 * <input>, or immediately for anything else (non-text inputs) — so normal
 * in-text cursor movement is never hijacked, but reaching the edge
 * continues into the next cell.
 *
 * A native <select> is deliberately left alone entirely (no cell-move, no
 * preventDefault) — a real bug caught by testing: capturing its arrow keys
 * to move cells also silently ate the browser's own built-in behavior of
 * using Up/Down/Left/Right to cycle the select's options, so choosing a
 * value went from "press an arrow key" to "you must click it with the
 * mouse." Leaving <select> untouched restores that native picking; moving
 * out of a select cell is Tab (or a click), same as any other web form.
 */
export function useGridKeyNav(rowCount: number, colCount: number) {
  const cells = useRef(new Map<string, FocusableEl>());

  const cellRef = useCallback((row: number, col: number) => (el: FocusableEl | null) => {
    const k = cellKey(row, col);
    if (el) cells.current.set(k, el);
    else cells.current.delete(k);
  }, []);

  const focusCell = useCallback((row: number, col: number) => {
    cells.current.get(cellKey(row, col))?.focus();
  }, []);

  const onCellKeyDown = useCallback((row: number, col: number) => (e: React.KeyboardEvent<FocusableEl>) => {
    const el = e.currentTarget;
    if (el.tagName === 'SELECT') return; // let the native picker have its own arrow keys
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        if (row > 0) focusCell(row - 1, col);
        return;
      case 'ArrowDown':
        e.preventDefault();
        if (row < rowCount - 1) focusCell(row + 1, col);
        return;
      case 'ArrowLeft': {
        const { atStart } = caretAtBoundary(el);
        if (!atStart) return;
        e.preventDefault();
        if (col > 0) focusCell(row, col - 1);
        return;
      }
      case 'ArrowRight': {
        const { atEnd } = caretAtBoundary(el);
        if (!atEnd) return;
        e.preventDefault();
        if (col < colCount - 1) focusCell(row, col + 1);
        return;
      }
      default:
        return;
    }
  }, [rowCount, colCount, focusCell]);

  return { cellRef, onCellKeyDown, focusCell };
}
